// frontend/components/WidgetWrapper.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-18
// Updated:      2026-09-16
//
// Description:  Reusable wrapper for dashboard widgets with title bar, optional close button,
//               and shared overflow / header interaction styling.
//
// Important:
//   - `showRemoveButton` defaults to true to preserve existing behaviour everywhere else.
//   - Fixed-layout pages such as Workflow Builder can explicitly disable the remove button.
//   - This avoids brittle route-based logic inside the wrapper.
//
// Responsive pass (2026-09-16):
//   - Wrapper remains the shared host on mobile; no alternate mobile wrapper is required.
//   - Remove control is always visible on touch/mobile widths because hover-only controls
//     are not reliably accessible on touch devices.
//   - Desktop preserves the existing hover-to-reveal remove control.
//   - Header reserves room for the mobile-visible remove control.
//   - Widget sizing, drag handle behaviour and content scrolling are unchanged.

import React, { useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";

const WidgetWrapper = ({
  title,
  onRemove,
  showRemoveButton = true,
  children,
}) => {
  const { theme } = useTheme();
  const [hover, setHover] = useState(false);
  const [click, setClick] = useState(false);

  const getCloseColor = () => {
    if (click) return theme.button_action?.click || "#000";
    if (hover) return theme.button_action?.hover || "#000";
    return theme.button_action?.normal || "#000";
  };

  const canRemove = showRemoveButton && typeof onRemove === "function";

  return (
    <div
      className="group relative flex h-full w-full min-w-0 flex-col overflow-hidden rounded bg-white p-3 shadow sm:p-4"
      style={{ position: "relative" }}
    >
      {/* Header */}
      <div
        className={`widget-drag-handle mb-2 min-w-0 break-words font-semibold ${
          canRemove ? "pr-8" : ""
        } cursor-move`}
        style={{ color: theme.accent_color, fontSize: "1rem" }}
      >
        {title}
      </div>

      {/* Close Button */}
      {canRemove && (
        <button
          onClick={onRemove}
          title="Remove Widget"
          aria-label={`Remove ${title} widget`}
          className="
            absolute right-2 top-2 z-10
            flex h-7 w-7 items-center justify-center
            rounded-full
            text-sm font-bold text-white
            opacity-100
            transition-opacity duration-200
            shadow-md
            md:h-6 md:w-6 md:opacity-0 md:group-hover:opacity-100
          "
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => {
            setHover(false);
            setClick(false);
          }}
          onMouseDown={() => setClick(true)}
          onMouseUp={() => setClick(false)}
          style={{
            backgroundColor: getCloseColor(),
            color: "#fff",
            cursor: "pointer",
          }}
        >
          ×
        </button>
      )}

      {/* Content */}
      <div className="min-h-0 min-w-0 flex-grow overflow-auto">{children}</div>
    </div>
  );
};

export default WidgetWrapper;
